import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks'
import {
  getSalesTeamPositionsData,
  selectCurrentUser,
} from '@user/store/authorization'
import {
  setSalespersonFilterOptions,
  setSalespersonFilterSelection,
} from '@user/store/map'
import { getSalespersonFilterPicklist } from '@user/store/users/hooks/useGetUserListForSalesTeamsByStructure'
import {
  IUserPreferenceGeneralListItemType,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { computeInitialSelection } from './useSalespersonFilter.utils'
import { useSalesPersonFilterEnabled } from '@user/store/tenantFeatures/hooks/useSalesPersonFilterEnabled'

// Fetches the salesperson picklist exactly once per map session (Rule 9) and
// seeds the initial selection from any persisted preference.
export function useSalespersonFilterPicklist() {
  const dispatch = useAppDispatch()
  const currentUser = useSelector(selectCurrentUser)
  const salesPersonFilterEnabled = useSalesPersonFilterEnabled()
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)
  const fetchedRef = useRef(false)

  const persistedValue = userGeneralPreferences?.find(
    (row) =>
      row?.type ===
      IUserPreferenceGeneralListItemType.MapViewFilteredSalespersonIds,
  )?.value

  useEffect(() => {
    if (!salesPersonFilterEnabled || fetchedRef.current || !currentUser) return
    fetchedRef.current = true

    const fetchPicklist = async () => {
      // SH-2168: Map View doesn't call useGetSalesTeamPositionsData itself
      // (only Profile/AddLead/AddOpportunity/etc. do), so a non-admin who
      // opens the map directly can reach here with no positions data loaded
      // yet — fetch it ourselves rather than send an empty team list.
      let salesTeamIds: number[] = []
      if (!currentUser.isAdmin) {
        salesTeamIds = currentUser.salesTeamPositionsData?.salesTeamIds ?? []
        if (!currentUser.salesTeamPositionsData) {
          const result = await dispatch(getSalesTeamPositionsData())
          if (getSalesTeamPositionsData.fulfilled.match(result)) {
            salesTeamIds = result.payload.salesTeamIds
          }
        }
      }

      const options = await getSalespersonFilterPicklist(salesTeamIds)
      if (!options) return
      dispatch(setSalespersonFilterOptions(options))
      dispatch(
        setSalespersonFilterSelection(
          computeInitialSelection(options, persistedValue),
        ),
      )
    }

    fetchPicklist()
    // persistedValue intentionally excluded — only read once, at the same
    // moment the picklist itself is fetched (Rule 9), not on every preference change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, dispatch, salesPersonFilterEnabled])
}
